import { useEffect, useState } from 'react';
import { formatAge } from '../../utils/formatAge';
import { formatNumber, formatEnumLabel } from '../../utils/format';
import { getRaceDisplayName, formatRulerWithTitle, getRaceUnitName, getUnitPointsLabel } from '../../constants/game';
import { raceChip, personalityChip } from '../../utils/dossierFlavor';
import { DossierFlavorChip } from '../dossier/DossierFlavorChip';
import { COLORS } from '../../constants/theme';
import { SectionHeader } from '../ui/SectionHeader';
import { ConfirmModal } from '../ConfirmModal';
import { INTEL_TYPE_META } from './intelTypeMeta';
import type {
  IntelReportDto,
  IntelDetail,
  CrystalBallData,
  SpyOnMilitaryData,
  SurveyData,
  InfiltrateData,
  SpyOnScienceData,
  SpyOnHomeDefenseData,
  SpyOnThroneData,
} from '../../types/intel';
import { parseIntelData } from '../../types/intel';

interface IntelDetailModalProps {
  /** SF-157: the target group's own already-loaded reports (no self-fetch) — a live lookup into
   *  the current targetGroups, not a snapshot copied at open time, so a delete inside the modal
   *  reflects immediately without closing/reopening it. */
  reports: IntelReportDto[];
  onClose: () => void;
  onShare?: (id: string) => void;
  onDelete?: (id: string) => void;
}

// Same fixed type order as MobileIntelDetailScreen; CE never appears (not part of any target group).
const TYPE_ORDER: IntelReportDto['type'][] = ['CB', 'SOM', 'SURVEY', 'INFILTRATE', 'SOS', 'SOHD', 'SOT'];

function orderedReports(reports: IntelReportDto[]): IntelReportDto[] {
  return [...reports].sort((a, b) => {
    const orderDiff = TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type);
    if (orderDiff !== 0) return orderDiff;
    return Date.parse(b.capturedAt) - Date.parse(a.capturedAt);
  });
}

export function IntelDetailModal({ reports, onClose, onShare, onDelete }: IntelDetailModalProps) {
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  // Close on Escape key — gated on this modal's own ConfirmModal not being open, so a single
  // Escape while confirming a delete only cancels the confirmation, never both at once (the
  // nested-modal Escape collision, Critical #8).
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && pendingDeleteId == null) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, pendingDeleteId]);

  if (reports.length === 0) return null;

  const sorted = orderedReports(reports);
  const first = sorted[0];
  const age = formatAge(first.capturedAt);

  function requestDelete(id: string) {
    setPendingDeleteId(id);
  }

  function confirmDelete() {
    if (pendingDeleteId) {
      onDelete?.(pendingDeleteId);
    }
    setPendingDeleteId(null);
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}` }}
        className="rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 id="modal-title" className="text-xl font-bold mb-1" style={{ color: COLORS.text.primary }}>
              {first.targetProvinceName}
            </h2>
            <p className="text-sm" style={{ color: COLORS.text.tertiary }}>
              {first.targetKingdomName} ({first.targetKingdomLocation}) • Freshest {age}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ color: COLORS.text.tertiary }}
            className="hover:opacity-80 text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Stacked cards, one per report, in fixed type order */}
        <div className="space-y-4">
          {sorted.map((report) => (
            <StackedReportCard
              key={report.id}
              report={report}
              onShare={onShare ? () => onShare(report.id) : undefined}
              onDelete={onDelete ? () => requestDelete(report.id) : undefined}
            />
          ))}
        </div>

        {/* Footer */}
        <div
          className="flex gap-3 mt-6 pt-4 border-t"
          style={{ borderColor: COLORS.border }}
        >
          <button
            onClick={onClose}
            style={{ background: COLORS.sectionHeader, color: COLORS.text.primary }}
            className="px-4 py-2 rounded hover:opacity-80 ml-auto"
          >
            Close
          </button>
        </div>
      </div>

      <ConfirmModal
        isOpen={pendingDeleteId != null}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDeleteId(null)}
        title="Delete this report?"
        confirmText="Delete"
        cancelText="Cancel"
      >
        This can't be undone.
      </ConfirmModal>
    </div>
  );
}

function StackedReportCard({ report, onShare, onDelete }: { report: IntelReportDto; onShare?: () => void; onDelete?: () => void }) {
  let content: ReturnType<typeof renderIntelContent>;
  try {
    content = renderIntelContent(parseIntelData(report));
  } catch {
    // Defensive guard — a parse/render failure for one stacked report must not crash the other
    // stacked cards for the same target.
    return (
      <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }} className="rounded p-4">
        <p style={{ color: COLORS.negative }} className="text-sm">Failed to load this report.</p>
      </div>
    );
  }

  const meta = INTEL_TYPE_META[report.type];

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span
            style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
              color: meta.fg, background: meta.bg, border: `1px solid ${meta.bd}`,
              borderRadius: 3, padding: '1px 6px',
            }}
          >
            {meta.full}
          </span>
          <span style={{ color: COLORS.text.muted, fontSize: 11 }}>{formatAge(report.capturedAt)}</span>
        </div>
        <div className="flex items-center gap-1">
          {onShare && report.sharing === 'PRIVATE' && (
            <button
              type="button"
              aria-label={`Share this ${meta.full} report`}
              onClick={onShare}
              style={{ background: 'none', border: 'none', color: COLORS.positive, cursor: 'pointer', fontSize: 13, padding: '2px 6px' }}
            >
              Share
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              aria-label={`Delete this ${meta.full} report`}
              onClick={onDelete}
              style={{ background: 'none', border: 'none', color: COLORS.negative, cursor: 'pointer', fontSize: 13, padding: '2px 6px' }}
            >
              Delete
            </button>
          )}
        </div>
      </div>
      {content}
    </div>
  );
}

function renderIntelContent(intel: IntelDetail) {
  switch (intel.type) {
    case 'CB':
      return renderCrystalBall(intel.data);
    case 'SOM':
      return renderSpyOnMilitary(intel.data);
    case 'SURVEY':
      return renderSurvey(intel.data);
    case 'INFILTRATE':
      return renderInfiltrate(intel.data);
    case 'SOS':
      return renderSpyOnScience(intel.data);
    case 'SOHD':
      return renderSpyOnHomeDefense(intel.data);
    case 'SOT':
      return renderSpyOnThrone(intel.data);
    case 'CE':
      return renderCrystalEye(intel.data);
  }
}

function renderCrystalEye(data: import('../../types/intel').CrystalEyeData) {
  return (
    <div
      style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }}
      className="rounded p-4"
    >
      <h4 className="text-base font-semibold mb-2" style={{ color: COLORS.text.primary }}>
        {data.kingdomName} ({data.location})
      </h4>
      <p className="text-xs mb-3" style={{ color: COLORS.text.muted }}>
        Recent events (ticks {data.fromTick}–{data.toTick})
      </p>
      {data.events.length === 0 ? (
        <p className="text-sm italic" style={{ color: COLORS.text.muted }}>
          No notable events in the lookback window.
        </p>
      ) : (
        <ul className="space-y-1 text-sm" style={{ color: COLORS.text.primary }}>
          {data.events.map((entry, idx) => (
            <li key={idx} className="flex gap-2">
              <span className="font-mono text-xs whitespace-nowrap" style={{ color: COLORS.text.muted }}>
                {entry.tick}
              </span>
              <span>{entry.summary}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function renderCrystalBall(data: CrystalBallData) {
  // Format ruler name with personality title and honor title
  const formattedRuler = formatRulerWithTitle(data.rulerName, data.personality, data.title);

  return (
    <>
      {/* Province Info */}
      <div
        style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }}
        className="rounded p-4"
      >
        <h3 className="text-sm font-semibold text-maroon-400 uppercase mb-2">Province Info</h3>
        <div className="flex gap-2 mb-3">
          <DossierFlavorChip meta={raceChip(data.race)} />
          <DossierFlavorChip meta={personalityChip(data.personality)} />
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Race:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{getRaceDisplayName(data.race)}</span>
          </div>
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Ruler:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{formattedRuler}</span>
          </div>
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Land:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{formatNumber(data.totalLand)} acres</span>
          </div>
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Building Efficiency:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{Math.round(data.buildingEfficiency * 100)}%</span>
          </div>
          {data.networth != null && (
            <div>
              <span style={{ color: COLORS.text.tertiary }}>Networth:</span>{' '}
              <span style={{ color: COLORS.gold }}>{formatNumber(data.networth)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Resources */}
      <div
        style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }}
        className="rounded p-4"
      >
        <h3 className="text-sm font-semibold text-maroon-400 uppercase mb-2">Resources</h3>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Gold:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{formatNumber(data.money)} gc</span>
          </div>
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Food:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{formatNumber(data.food)} bushels</span>
          </div>
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Runes:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{formatNumber(data.runes)}</span>
          </div>
        </div>
      </div>

      {/* Population */}
      <div
        style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }}
        className="rounded p-4"
      >
        <h3 className="text-sm font-semibold text-maroon-400 uppercase mb-2">Population</h3>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Peasants:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{formatNumber(data.peasants)}</span>
          </div>
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Soldiers:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{formatNumber(data.soldiers)}</span>
          </div>
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Off Specs:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{formatNumber(data.offenseSpecialists)}</span>
          </div>
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Def Specs:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{formatNumber(data.defenseSpecialists)}</span>
          </div>
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Elites:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{formatNumber(data.elites)}</span>
          </div>
          <div>
            <span style={{ color: COLORS.text.tertiary }}>War Horses:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{formatNumber(data.warHorses)}</span>
          </div>
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Prisoners:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{formatNumber(data.prisoners)}</span>
          </div>
          <div style={{ color: COLORS.text.muted }}>
            Thieves: HIDDEN • Wizards: HIDDEN
          </div>
        </div>
      </div>

      {/* Military */}
      <div
        style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }}
        className="rounded p-4"
      >
        <h3 className="text-sm font-semibold text-maroon-400 uppercase mb-2">Military</h3>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Offense:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{formatNumber(data.offensePoints)}</span>
          </div>
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Defense:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{formatNumber(data.defensePoints)}</span>
          </div>
        </div>
      </div>

      {/* Honor */}
      <div
        style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }}
        className="rounded p-4"
      >
        <h3 className="text-sm font-semibold text-maroon-400 uppercase mb-2">Honor</h3>
        <div className="text-sm">
          <span style={{ color: COLORS.text.tertiary }}>Honor:</span>{' '}
          <span style={{ color: COLORS.text.primary }}>{formatNumber(data.honour)} ({data.title})</span>
        </div>
      </div>

      {/* Active Effects */}
      {data.activeEffects.length > 0 && (
        <div
          style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }}
          className="rounded p-4"
        >
          <h3 className="text-sm font-semibold text-maroon-400 uppercase mb-2">Active Effects</h3>
          <ul className="text-sm space-y-1">
            {data.activeEffects.map((effect, i) => (
              <li key={i} style={{ color: COLORS.text.secondary }}>
                • {effect.effectType} ({effect.indefinite ? 'active' : `${effect.ticksRemaining} days remaining`})
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

function renderSpyOnMilitary(data: SpyOnMilitaryData) {
  const race = data.race;

  const ARMY_UNITS: Array<{ label: string; value: number | null }> = [
    { label: 'Generals', value: data.generals ?? null },
    { label: `Soldiers ${getUnitPointsLabel(race ?? undefined, 'soldier')}`, value: data.soldiers },
    { label: `${getRaceUnitName(race ?? undefined, 'offense')} ${getUnitPointsLabel(race ?? undefined, 'offense')}`, value: data.offensiveSpecs },
    { label: `${getRaceUnitName(race ?? undefined, 'defense')} ${getUnitPointsLabel(race ?? undefined, 'defense')}`, value: data.defensiveSpecs },
    { label: `${getRaceUnitName(race ?? undefined, 'elite')} ${getUnitPointsLabel(race ?? undefined, 'elite')}`, value: data.elites },
  ];

  const TRAINING_UNIT_TYPES = [
    `${getRaceUnitName(race ?? undefined, 'offense')} ${getUnitPointsLabel(race ?? undefined, 'offense')}`,
    `${getRaceUnitName(race ?? undefined, 'defense')} ${getUnitPointsLabel(race ?? undefined, 'defense')}`,
    `${getRaceUnitName(race ?? undefined, 'elite')} ${getUnitPointsLabel(race ?? undefined, 'elite')}`,
    'Thieves',
  ];

  return (
    <>
      {/* Section Title - matching Military Affairs */}
      <SectionHeader title="Military Intelligence" />

      {/* Intro and Effectiveness - matching Military Affairs */}
      <div
        style={{ background: COLORS.card, borderLeft: `1px solid ${COLORS.border}`, borderRight: `1px solid ${COLORS.border}`, borderBottom: `1px solid ${COLORS.border}` }}
        className="p-4 text-sm mb-4"
      >
        <p className="mb-4" style={{ color: COLORS.text.secondary }}>
          Your thieves have gathered intelligence on the target province's military forces.
          This report details their troops at home, training activity, and military efficiency.
        </p>

        <p className="font-medium mb-2" style={{ color: COLORS.text.primary }}>Military Effectiveness</p>
        <p className="mb-3" style={{ color: COLORS.text.secondary }}>
          Our military effectiveness is determined by the wage rates of our armed forces. Higher
          wages can drive up productivity. Any changes to pay will take time to change our
          efficiency.
        </p>

        {data.networth != null && (
          <p className="mb-3" style={{ color: COLORS.text.secondary }}>
            Networth: <span style={{ color: COLORS.gold, fontWeight: 600 }}>{formatNumber(data.networth)}</span>
          </p>
        )}

        <div
          style={{ background: COLORS.sectionHeader }}
          className="rounded p-3 text-center"
        >
          <div className="mb-1" style={{ color: COLORS.text.secondary }}>
            Offensive Military Effectiveness:{' '}
            <span className="font-medium" style={{ color: COLORS.text.primary }}>
              {data.offensiveME != null ? `${(data.offensiveME * 100).toFixed(1)}%` : <span style={{ color: COLORS.text.tertiary }} className="italic">Unknown</span>}
            </span>
          </div>
          <div className="mb-1" style={{ color: COLORS.text.secondary }}>
            Defensive Military Effectiveness:{' '}
            <span className="font-medium" style={{ color: COLORS.text.primary }}>
              {`${(data.defensiveME * 100).toFixed(1)}%`}
            </span>
          </div>
          <div className="mb-1" style={{ color: COLORS.text.secondary }}>
            Raw Offensive Points at Home:{' '}
            <span className="font-medium" style={{ color: COLORS.text.primary }}>
              {data.offensePoints != null ? formatNumber(Math.round(data.offensePoints)) : <span style={{ color: COLORS.text.tertiary }} className="italic">Unknown</span>}
            </span>
          </div>
          <div style={{ color: COLORS.text.secondary }}>
            Raw Defensive Points at Home:{' '}
            <span className="font-medium" style={{ color: COLORS.text.primary }}>
              {data.defensePoints != null ? formatNumber(Math.round(data.defensePoints)) : <span style={{ color: COLORS.text.tertiary }} className="italic">Unknown</span>}
            </span>
          </div>
        </div>
      </div>

      {/* Army Table - matching Military Affairs */}
      <div
        style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }}
        className="rounded overflow-hidden mb-4"
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-blue-900" style={{ color: COLORS.text.primary }}>
              <th className="py-2 px-3 text-left font-medium">Army Number:</th>
              <th className="py-2 px-3 text-center font-medium">Standing</th>
            </tr>
          </thead>
          <tbody>
            {ARMY_UNITS.map((unit, idx) => (
              <tr
                key={unit.label}
                style={{
                  borderTop: `1px solid ${COLORS.border}`,
                  background: idx % 2 === 0 ? COLORS.tableRowAlt : COLORS.card,
                }}
              >
                <td className="py-1.5 px-3 bg-blue-900 font-medium" style={{ color: COLORS.text.primary }}>{unit.label}</td>
                <td className="py-1.5 px-3 text-center" style={{ color: COLORS.text.primary }}>
                  {unit.value != null ? formatNumber(unit.value) : <span style={{ color: COLORS.text.tertiary }} className="italic">Unknown</span>}
                </td>
              </tr>
            ))}
            <tr style={{ borderTop: `1px solid ${COLORS.border}`, background: COLORS.tableRowAlt }}>
              <td className="py-1.5 px-3 bg-blue-900 font-medium" style={{ color: COLORS.text.primary }}>Offensive Pts</td>
              <td className="py-1.5 px-3 text-center" style={{ color: COLORS.text.primary }}>
                {data.offensePoints != null ? formatNumber(Math.round(data.offensePoints)) : <span style={{ color: COLORS.text.tertiary }} className="italic">Unknown</span>}
              </td>
            </tr>
            <tr style={{ borderTop: `1px solid ${COLORS.border}`, background: COLORS.card }}>
              <td className="py-1.5 px-3 bg-blue-900 font-medium" style={{ color: COLORS.text.primary }}>Defensive Pts</td>
              <td className="py-1.5 px-3 text-center" style={{ color: COLORS.text.primary }}>
                {data.defensePoints != null ? formatNumber(Math.round(data.defensePoints)) : <span style={{ color: COLORS.text.tertiary }} className="italic">Unknown</span>}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Training Queue - matching Military Affairs 24-tick grid */}
      <div
        style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }}
        className="rounded overflow-hidden overflow-x-auto"
      >
        <table className="w-full text-sm whitespace-nowrap">
          <thead>
            <tr className="bg-blue-900" style={{ color: COLORS.text.primary }}>
              <th className="py-2 px-2 text-left font-medium sticky left-0 bg-blue-900">
                Army Unit
              </th>
              {Array.from({ length: 24 }, (_, i) => (
                <th key={i} className="py-2 px-1 text-center font-medium min-w-[32px]">
                  {i + 1}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TRAINING_UNIT_TYPES.map((unit, rowIdx) => (
              <tr
                key={unit}
                style={{
                  borderTop: `1px solid ${COLORS.border}`,
                  background: rowIdx % 2 === 0 ? COLORS.tableRowAlt : COLORS.card,
                }}
              >
                <td className="py-1.5 px-2 font-medium sticky left-0 bg-inherit" style={{ color: COLORS.text.primary }}>
                  {unit}
                </td>
                {Array.from({ length: 24 }, (_, day) => (
                  <td key={day} className="py-1.5 px-1 text-center" style={{ color: COLORS.text.primary }}>
                    -
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {(data.queueLength > 0 || data.armyCount > 0) && (
          <div
            className="px-3 py-2 text-xs"
            style={{ color: COLORS.text.tertiary, borderTop: `1px solid ${COLORS.border}` }}
          >
            {data.queueLength > 0 && (
              <div>
                Intelligence indicates {data.queueLength} unit{data.queueLength !== 1 ? 's' : ''} in training
                {data.nextCompletionTicks != null && (
                  <> (fully trained in {data.nextCompletionTicks} days)</>
                )}
              </div>
            )}
            {data.armyCount > 0 && (
              <div>
                {data.armyCount} arm{data.armyCount > 1 ? 'ies' : 'y'} currently deployed
                {data.nextReturnTicks != null && (
                  <> (earliest return: {data.nextReturnTicks} days)</>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function renderSurvey(data: SurveyData) {
  return (
    <>
      <div
        style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }}
        className="rounded p-4"
      >
        <h3 className="text-sm font-semibold text-maroon-400 uppercase mb-2">Land</h3>
        <div className="space-y-2 text-sm">
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Total Land:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{formatNumber(data.totalLand)} acres</span>
          </div>
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Barren Land:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{formatNumber(data.barrenLand)} acres</span>
          </div>
          {data.networth != null && (
            <div>
              <span style={{ color: COLORS.text.tertiary }}>Networth:</span>{' '}
              <span style={{ color: COLORS.gold }}>{formatNumber(data.networth)}</span>
            </div>
          )}
        </div>
      </div>

      <div
        style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }}
        className="rounded p-4"
      >
        <h3 className="text-sm font-semibold text-maroon-400 uppercase mb-2">Buildings</h3>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          {Object.entries(data.buildings).map(([building, count]) => (
            <div key={building}>
              <span style={{ color: COLORS.text.tertiary }}>{formatBuildingName(building)}:</span>{' '}
              <span style={{ color: COLORS.text.primary }}>{formatNumber(count)}</span>
            </div>
          ))}
        </div>
      </div>

      {data.construction && data.construction.length > 0 && (
        <div
          style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }}
          className="rounded p-4"
        >
          <h3 className="text-sm font-semibold text-maroon-400 uppercase mb-2">Construction Queue</h3>
          <ul className="text-sm space-y-1">
            {data.construction.map((item, i) => (
              <li key={i} style={{ color: COLORS.text.secondary }}>
                • {formatBuildingName(item.buildingType)}: {formatNumber(item.acres)} acres ({item.ticksRemaining} ticks)
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.exploration && data.exploration.length > 0 && (
        <div
          style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }}
          className="rounded p-4"
        >
          <h3 className="text-sm font-semibold text-maroon-400 uppercase mb-2">Exploration Queue</h3>
          <ul className="text-sm space-y-1">
            {data.exploration.map((item, i) => (
              <li key={i} style={{ color: COLORS.text.secondary }}>
                • {formatNumber(item.acres)} acres ({item.ticksRemaining} ticks)
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

// Helper to format building names for display
function formatBuildingName(name: string): string {
  const BUILDING_NAMES: Record<string, string> = {
    NONE: 'Barren Lands',
    HOME: 'Homes',
    FARM: 'Farms',
    MILL: 'Mills',
    BANK: 'Banks',
    TRAINING_GROUND: 'Training Grounds',
    ARMOURY: 'Armouries',
    MILITARY_BARRACK: 'Barracks',
    FORT: 'Forts',
    GUILD: 'Guilds',
    TOWER: 'Towers',
    THIEVES_DEN: "Thieves' Dens",
    WATCH_TOWER: 'Watch Towers',
    SCHOOL: 'Schools',
    LIBRARY: 'Libraries',
    HOSPITAL: 'Hospitals',
    GUARD_STATION: 'Guard Stations',
    STABLE: 'Stables',
    DUNGEON: 'Dungeons',
  };
  return BUILDING_NAMES[name] || name.replace(/_/g, ' ');
}

function renderInfiltrate(data: InfiltrateData) {
  return (
    <div
      style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }}
      className="rounded p-4"
    >
      <h3 className="text-sm font-semibold text-maroon-400 uppercase mb-2">Thief Intelligence</h3>
      <div className="space-y-2 text-sm">
        <div>
          <span style={{ color: COLORS.text.tertiary }}>Thieves:</span>{' '}
          <span style={{ color: COLORS.text.primary }}>{formatNumber(data.thieves)}</span>
        </div>
        <div>
          <span style={{ color: COLORS.text.tertiary }}>Raw TPA:</span>{' '}
          <span style={{ color: COLORS.text.primary }}>{data.rawTPA.toFixed(2)}</span>
        </div>
        {data.networth != null && (
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Networth:</span>{' '}
            <span style={{ color: COLORS.gold }}>{formatNumber(data.networth)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Sciences in classic Utopia order for intel display
const INTEL_SCIENCES: Record<string, { name: string; description: string }> = {
  ALCHEMY: { name: 'Alchemy', description: 'Increases gold income' },
  TOOLS: { name: 'Tools', description: 'Increases building efficiency' },
  HOUSING: { name: 'Housing', description: 'Increases max population' },
  FOOD: { name: 'Food', description: 'Increases food production' },
  MILITARY: { name: 'Military', description: 'Increases military effectiveness' },
  THIEVERY: { name: 'Crime', description: 'Improves thievery operations' },
  CHANNELING: { name: 'Channeling', description: 'Improves magic effectiveness' },
};

const SCIENCE_TYPE_ORDER = ['ALCHEMY', 'TOOLS', 'HOUSING', 'FOOD', 'MILITARY', 'THIEVERY', 'CHANNELING'];

function renderSpyOnScience(data: SpyOnScienceData) {
  // Build a lookup from the sciences list
  const scienceMap = new Map(data.sciences.map((s) => [s.scienceType, s]));

  return (
    <>
      {/* Section Title - matching council ArtsAndSciences */}
      <SectionHeader title="Arts & Sciences" />

      {/* Intro + Research Rate Info */}
      <div
        style={{
          background: COLORS.card,
          borderLeft: `1px solid ${COLORS.border}`,
          borderRight: `1px solid ${COLORS.border}`,
          borderBottom: `1px solid ${COLORS.border}`,
        }}
        className="p-4 text-sm mb-4"
      >
        <p className="mb-4" style={{ color: COLORS.text.secondary }}>
          Your thieves have investigated the target province's scientific progress. This report
          details their research rate and the books of knowledge they have accumulated.
        </p>

        <div style={{ background: COLORS.sectionHeader }} className="rounded p-3">
          <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
            <div className="flex justify-between">
              <span style={{ color: COLORS.text.tertiary }}>Research Rate:</span>
              <span style={{ color: COLORS.text.primary }}>{formatEnumLabel(data.researchRate)}</span>
            </div>
            {data.networth != null && (
              <div className="flex justify-between">
                <span style={{ color: COLORS.text.tertiary }}>Networth:</span>
                <span style={{ color: COLORS.gold }}>{formatNumber(data.networth)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Science Progress Table - matching council ArtsAndSciences */}
      <div
        style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }}
        className="rounded overflow-hidden"
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-blue-900" style={{ color: COLORS.text.primary }}>
              <th className="py-2 px-3 text-left font-medium">Technology</th>
              <th className="py-2 px-3 text-right font-medium">Books</th>
              <th className="py-2 px-3 text-right font-medium">Effect</th>
            </tr>
          </thead>
          <tbody>
            {SCIENCE_TYPE_ORDER.map((scienceType, idx) => {
              const sciData = scienceMap.get(scienceType);
              const display = INTEL_SCIENCES[scienceType];
              if (!sciData || !display) return null;
              return (
                <tr
                  key={scienceType}
                  style={{
                    borderTop: `1px solid ${COLORS.border}`,
                    background: idx % 2 === 0 ? COLORS.tableRowAlt : COLORS.card,
                  }}
                >
                  <td className="py-1.5 px-3">
                    <div className="font-medium" style={{ color: COLORS.text.primary }}>{display.name}</div>
                    <div className="text-xs" style={{ color: COLORS.text.muted }}>{display.description}</div>
                  </td>
                  <td className="py-1.5 px-3 text-right" style={{ color: COLORS.text.primary }}>
                    {formatNumber(sciData.booksKnown)}
                  </td>
                  <td className="py-1.5 px-3 text-right" style={{ color: COLORS.text.primary }}>
                    {(sciData.effectPercentage * 100).toFixed(1)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function renderSpyOnHomeDefense(data: SpyOnHomeDefenseData) {
  return (
    <div
      style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }}
      className="rounded p-4"
    >
      <h3 className="text-sm font-semibold text-maroon-400 uppercase mb-2">Home Defense</h3>
      <div className="space-y-2 text-sm">
        <div>
          <span style={{ color: COLORS.text.tertiary }}>Total Home Defense:</span>{' '}
          <span style={{ color: COLORS.text.primary }}>{formatNumber(Math.round(data.homeDefense))}</span>
        </div>
        {data.networth != null && (
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Networth:</span>{' '}
            <span style={{ color: COLORS.gold }}>{formatNumber(data.networth)}</span>
          </div>
        )}
        <div className="text-xs" style={{ color: COLORS.text.tertiary }}>100% accurate — includes all modifiers</div>
      </div>
    </div>
  );
}

function renderSpyOnThrone(data: SpyOnThroneData) {
  return (
    <>
      <div
        style={{ background: COLORS.card, border: `1px solid ${COLORS.border}` }}
        className="rounded p-4"
      >
        <h3 className="text-sm font-semibold uppercase mb-2" style={{ color: COLORS.text.primary }}>
          Throne Room Intelligence
        </h3>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Ruler:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{data.ruler}</span>
          </div>
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Race:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{getRaceDisplayName(data.race)}</span>
          </div>
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Thieves:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{formatNumber(data.thieves)}</span>
          </div>
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Wizards:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{formatNumber(data.wizards)}</span>
          </div>
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Mana Rating:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{(data.manaRating * 100).toFixed(1)}%</span>
          </div>
          <div>
            <span style={{ color: COLORS.text.tertiary }}>Net Income/day:</span>{' '}
            <span style={{ color: COLORS.text.primary }}>{formatNumber(Math.round(data.netIncomePerTick))} gc</span>
          </div>
          {data.networth != null && (
            <div>
              <span style={{ color: COLORS.text.tertiary }}>Networth:</span>{' '}
              <span style={{ color: COLORS.gold }}>{formatNumber(data.networth)}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
